/**
 * Saved Searches API utilities
 *
 * Provides functions for managing user saved search queries.
 */

import api from './api';

export interface SearchParams {
  q?: string;
  workType?: string;
  materials?: string;
  techniques?: string;
  creators?: string;
  yearFrom?: number;
  yearTo?: number;
  dateFrom?: number;
  dateTo?: number;
  heightFrom?: number;
  heightTo?: number;
  widthFrom?: number;
  widthTo?: number;
  provenance?: string;
  title?: string;
  sortBy?: string;
  dimensionUnit?: string;
  /**
   * Phase 2 follow-up B polish: persisted scope selection, in the
   * comma-separated wire form the search router accepts (e.g. "own,public"
   * or "own,delegate:USR-X:col-1"). Round-tripped via
   * utils/searchScopes.serializeScopes / parseScopes.
   */
  scopes?: string;
}

export interface SavedSearch {
  searchId: string;
  name: string;
  searchParams: SearchParams;
  createdAt: string;
  updatedAt: string;
}

export interface SavedSearchListResponse {
  items: SavedSearch[];
  total: number;
}

export interface UpdateSavedSearchRequest {
  name?: string;
  searchParams?: SearchParams;
}

/**
 * Get all saved searches for the current user.
 */
export const getSavedSearches = async (): Promise<SavedSearchListResponse> => {
  const response = await api.get('/api/saved-searches');
  return response.data;
};

/**
 * Create a new saved search.
 */
export const createSavedSearch = async (
  name: string,
  searchParams: SearchParams
): Promise<SavedSearch> => {
  const response = await api.post('/api/saved-searches', {
    name,
    searchParams,
  });
  return response.data;
};

/**
 * Update an existing saved search.
 */
export const updateSavedSearch = async (
  searchId: string,
  updates: UpdateSavedSearchRequest
): Promise<SavedSearch> => {
  const response = await api.put(`/api/saved-searches/${encodeURIComponent(searchId)}`, updates);
  return response.data;
};

/**
 * Delete a saved search.
 */
export const deleteSavedSearch = async (searchId: string): Promise<void> => {
  await api.delete(`/api/saved-searches/${encodeURIComponent(searchId)}`);
};
